import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ReaderChatModal.css";

const IMG_BASE = process.env.REACT_APP_API_URL?.replace("/api", "") ?? "";
const UNREAD_DIVIDER_DURATION_MS = 20000;
const CHAT_MESSAGES_LIMIT = 43;

function ReaderChatAvatar({ user, className = "" }) {
  const [failed, setFailed] = useState(false);

  if (!user?.profile_picture || failed) {
    return (
      <div className={`reader-chat-avatar reader-chat-avatar--placeholder ${className}`.trim()}>
        <span className="material-symbols-rounded">person</span>
      </div>
    );
  }

  return (
    <img
      src={`${IMG_BASE}/${user.profile_picture}`}
      alt={user.display_name || "User"}
      className={`reader-chat-avatar ${className}`.trim()}
      onError={() => setFailed(true)}
    />
  );
}

function formatConversationMembers(memberNames = []) {
  const cleanedNames = memberNames.filter(Boolean);

  if (cleanedNames.length <= 3) {
    return cleanedNames.join(", ");
  }

  const visibleNames = cleanedNames.slice(0, 3).join(", ");
  const remainingCount = cleanedNames.length - 3;
  return `${visibleNames} (+${remainingCount} others)`;
}

function getConversationSubtitle(conversation, currentUserId) {
  const orderedMembers = (conversation?.members || [])
    .slice()
    .sort((left, right) => {
      if (Number(left.id) === Number(currentUserId)) return -1;
      if (Number(right.id) === Number(currentUserId)) return 1;
      return 0;
    })
    .map((member) => member.display_name)
    .filter(Boolean);

  return formatConversationMembers(orderedMembers);
}

function parseMessageDate(dateString) {
  if (!dateString) return null;
  const parsed = new Date(dateString);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatUnreadDividerTime(dateString) {
  const parsed = parseMessageDate(dateString);
  if (!parsed) return "";
  return parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit"
  });
}

function ReaderChatModal({ isOpen, onClose, initialQuote = null, onInitialQuoteApplied }) {
  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiFetch = useCallback((url, options = {}) => {
    const rememberToken = localStorage.getItem("rememberToken") || "";
    const nextHeaders = {
      ...(options.headers || {})
    };
    if (rememberToken && !nextHeaders["X-Remember-Token"]) {
      nextHeaders["X-Remember-Token"] = rememberToken;
    }
    return fetch(url, {
      credentials: "include",
      cache: "no-store",
      ...options,
      headers: nextHeaders
    });
  }, []);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadConversationCounts, setUnreadConversationCounts] = useState({});
  const [latestConversationMessageIds, setLatestConversationMessageIds] = useState({});
  const [draftMessage, setDraftMessage] = useState("");
  const [quotedMessage, setQuotedMessage] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [createChatError, setCreateChatError] = useState("");
  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [openMenuConversation, setOpenMenuConversation] = useState(null);
  const [composerHeight, setComposerHeight] = useState(88);
  const [chatEditMode, setChatEditMode] = useState(null);
  const [editChatName, setEditChatName] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [selectedNewMembers, setSelectedNewMembers] = useState([]);
  const [isChatEditing, setIsChatEditing] = useState(false);
  const [chatEditError, setChatEditError] = useState("");
  const [isMemberSearchLoading, setIsMemberSearchLoading] = useState(false);
  const [memberSearchError, setMemberSearchError] = useState("");
  const [openSwipeMessageId, setOpenSwipeMessageId] = useState(null);
  const [quotedBubbleWidths, setQuotedBubbleWidths] = useState({});
  const [highlightedReplyTargetId, setHighlightedReplyTargetId] = useState(null);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isClosing, setIsClosing] = useState(false);
  const [unreadDividerMessageId, setUnreadDividerMessageId] = useState(null);
  const [unreadDividerLabel, setUnreadDividerLabel] = useState("");
  const searchRequestRef = useRef(0);
  const memberSearchRequestRef = useRef(0);
  const messagesRequestRef = useRef(0);
  const olderMessagesRequestRef = useRef(0);
  const messagesScrollRef = useRef(null);
  const composerRef = useRef(null);
  const messageNodeRefs = useRef(new Map());
  const quoteResizeObserversRef = useRef(new Map());
  const unreadDismissTimerRef = useRef(null);
  const unreadDividerConversationRef = useRef(null);
  const hasAutoJumpedUnreadRef = useRef(false);
  const shouldEvaluateUnreadOnEntryRef = useRef(false);
  const shouldScrollToBottomOnEntryRef = useRef(false);
  const entryLastSeenMessageIdRef = useRef(null);
  const messagesRef = useRef([]);
  const conversationsRef = useRef([]);
  const activeConversationIdRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const forceScrollToBottomRef = useRef(false);
  const olderMessagesRestoreRef = useRef(null);
  const hasPagedOlderMessagesRef = useRef(false);
  const hasMoreOlderMessagesRef = useRef(true);
  const isLoadingOlderMessagesRef = useRef(false);
  const highlightReplyTimerRef = useRef(null);
  const swipeStateRef = useRef({
    messageId: null,
    startX: 0,
    startY: 0,
    deltaX: 0,
    horizontal: false
  });
  const trimmedUserQuery = userQuery.trim();
  const trimmedMemberQuery = memberQuery.trim();
  const menuConversation = openMenuConversation || activeConversation;

  const getSeenMessageStorageKey = useCallback(
    (conversationId) => `reader-chat-last-seen-${currentUser?.id || 0}-${conversationId}`,
    [currentUser]
  );

  const getLatestVisibleMessageId = useCallback((items = []) => {
    const latestMessage = [...items]
      .reverse()
      .find((item) => item?.message_type !== "system" && Number(item?.id) > 0);
    return Number(latestMessage?.id || 0);
  }, []);

  const hasCurrentUserParticipation = useCallback(
    (items = []) =>
      items.some(
        (message) =>
          message?.message_type !== "system" &&
          Number(message?.id) > 0 &&
          Number(message?.user_id) === Number(currentUser.id)
      ),
    [currentUser.id]
  );

  const mergeMessagesById = useCallback((existingMessages = [], incomingMessages = []) => {
    const mergedMessages = new Map();

    [...existingMessages, ...incomingMessages].forEach((message) => {
      if (!message?.id) return;
      mergedMessages.set(Number(message.id), message);
    });

    return Array.from(mergedMessages.values()).sort(
      (left, right) => Number(left.id) - Number(right.id)
    );
  }, []);

  const readLastSeenMessageId = useCallback(
    (conversationId) => {
      if (!conversationId) return 0;
      try {
        return Number(localStorage.getItem(getSeenMessageStorageKey(conversationId)) || 0);
      } catch (error) {
        return 0;
      }
    },
    [getSeenMessageStorageKey]
  );

  const writeLastSeenMessageId = useCallback(
    (conversationId, messageId) => {
      if (!conversationId || !messageId) return;
      try {
        localStorage.setItem(getSeenMessageStorageKey(conversationId), String(messageId));
      } catch (error) {}
    },
    [getSeenMessageStorageKey]
  );

  const clearUnreadDivider = useCallback(
    ({ markSeen = false, messageId } = {}) => {
      window.clearTimeout(unreadDismissTimerRef.current);
      unreadDismissTimerRef.current = null;
      setUnreadDividerMessageId(null);
      setUnreadDividerLabel("");
      unreadDividerConversationRef.current = null;
      hasAutoJumpedUnreadRef.current = false;
      shouldEvaluateUnreadOnEntryRef.current = false;
      shouldScrollToBottomOnEntryRef.current = false;

      if (markSeen) {
        const conversationId = activeConversationIdRef.current;
        const latestVisibleMessageId = Number(messageId || getLatestVisibleMessageId(messagesRef.current));
        if (conversationId && latestVisibleMessageId) {
          writeLastSeenMessageId(conversationId, latestVisibleMessageId);
        }
      }
    },
    [getLatestVisibleMessageId, writeLastSeenMessageId]
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    isLoadingOlderMessagesRef.current = isLoadingOlderMessages;
  }, [isLoadingOlderMessages]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    activeConversationIdRef.current = activeConversation?.id || null;
  }, [activeConversation?.id]);

  const mergeConversationState = useCallback((nextConversation) => {
    if (!nextConversation?.id) return;

    setConversations((previous) => {
      const existingIndex = previous.findIndex(
        (conversation) => Number(conversation.id) === Number(nextConversation.id)
      );

      if (existingIndex === -1) {
        return [nextConversation, ...previous];
      }

      const updated = [...previous];
      updated[existingIndex] = {
        ...updated[existingIndex],
        ...nextConversation
      };
      return updated;
    });

    setActiveConversation((previous) =>
      Number(previous?.id) === Number(nextConversation.id)
        ? { ...previous, ...nextConversation }
        : previous
    );
  }, []);

  const loadConversations = useCallback((signal, { silent = false } = {}) => {
    if (!apiUrl || !currentUser?.id) return Promise.resolve();

    if (!silent) setIsConversationsLoading(true);

    return apiFetch(`${apiUrl}/get-conversations.php?uid=${currentUser.id}`, {
      credentials: "include",
      cache: "no-store",
      signal
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data?.success) return;
        const nextConversations = Array.isArray(data.conversations) ? data.conversations : [];
        setConversations(nextConversations);

        if (activeConversation) {
          const nextActiveConversation = nextConversations.find(
            (conversation) => conversation.id === activeConversation.id
          );
          if (nextActiveConversation) {
            setActiveConversation(nextActiveConversation);
          } else {
            setActiveConversation(null);
            setMessages([]);
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!signal?.aborted && !silent) {
          setIsConversationsLoading(false);
        }
      });
  }, [activeConversation, apiUrl, currentUser]);

  const loadMessages = useCallback((conversationId, signal, { silent = false, beforeId = 0 } = {}) => {
    if (!apiUrl || !currentUser?.id || !conversationId) return Promise.resolve();

    const isLoadingOlderBatch = Number(beforeId) > 0;
    const requestId = isLoadingOlderBatch
      ? ++olderMessagesRequestRef.current
      : ++messagesRequestRef.current;
    if (!silent) {
      setIsMessagesLoading(true);
      setMessageError("");
    }
    if (isLoadingOlderBatch) {
      setIsLoadingOlderMessages(true);
    }

    const query = new URLSearchParams({
      uid: String(currentUser.id),
      conversation_id: String(conversationId),
      limit: String(CHAT_MESSAGES_LIMIT)
    });
    if (beforeId) {
      query.set("before_id", String(beforeId));
    }

    return apiFetch(
      `${apiUrl}/get-messages.php?${query.toString()}`,
      {
        credentials: "include",
        cache: "no-store",
        signal
      }
    )
      .then((response) => response.json())
      .then((data) => {
        const isStaleRequest = isLoadingOlderBatch
          ? olderMessagesRequestRef.current !== requestId
          : messagesRequestRef.current !== requestId;
        if (isStaleRequest || !data?.success) return;
        const nextMessages = Array.isArray(data.messages) ? data.messages : [];

        if (isLoadingOlderBatch) {
          hasMoreOlderMessagesRef.current = nextMessages.length >= CHAT_MESSAGES_LIMIT;
        } else if (!hasPagedOlderMessagesRef.current) {
          hasMoreOlderMessagesRef.current = nextMessages.length >= CHAT_MESSAGES_LIMIT;
        }

        setMessages((previous) => {
          if (isLoadingOlderBatch) {
            return mergeMessagesById(nextMessages, previous);
          }
          if (hasPagedOlderMessagesRef.current && previous.length > nextMessages.length) {
            return mergeMessagesById(previous, nextMessages);
          }
          return nextMessages;
        });

        if (isLoadingOlderBatch && nextMessages.length > 0) {
          hasPagedOlderMessagesRef.current = true;
        }

        if (data.conversation) {
          setActiveConversation((previous) =>
            previous?.id === data.conversation.id ? data.conversation : previous
          );
          setConversations((previous) =>
            previous.map((conversation) =>
              conversation.id === data.conversation.id
                ? { ...conversation, ...data.conversation }
                : conversation
            )
          );
        }

        setUnreadConversationCounts((previous) => {
          if (!conversationId || previous[String(conversationId)] == null) return previous;
          const visibleMessages = nextMessages.filter(
            (message) => message?.message_type !== "system" && Number(message?.id) > 0
          );
          const storedLastSeenMessageId = readLastSeenMessageId(conversationId);
          const effectiveLastSeenMessageId =
            storedLastSeenMessageId ||
            (hasCurrentUserParticipation(visibleMessages)
              ? getLatestVisibleMessageId(visibleMessages)
              : 0);
          if (!storedLastSeenMessageId && effectiveLastSeenMessageId) {
            writeLastSeenMessageId(conversationId, effectiveLastSeenMessageId);
          }
          const unreadCount = visibleMessages.filter(
            (message) =>
              Number(message.id) > Number(effectiveLastSeenMessageId) &&
              Number(message.user_id) !== Number(currentUser.id)
          ).length;
          if (previous[String(conversationId)] === unreadCount) return previous;
          return { ...previous, [String(conversationId)]: unreadCount };
        });
      })
      .catch((error) => {
        const isStaleRequest = isLoadingOlderBatch
          ? olderMessagesRequestRef.current !== requestId
          : messagesRequestRef.current !== requestId;
        if (signal?.aborted || silent || isStaleRequest) return;
        setMessageError(error?.message || "Couldn't load messages right now.");
      })
      .finally(() => {
        if (!signal?.aborted && isLoadingOlderBatch) {
          setIsLoadingOlderMessages(false);
        }
        if (
          !silent &&
          !signal?.aborted &&
          !isLoadingOlderBatch &&
          messagesRequestRef.current === requestId
        ) {
          setIsMessagesLoading(false);
        }
      });
  }, [apiUrl, currentUser, getLatestVisibleMessageId, hasCurrentUserParticipation, mergeMessagesById, readLastSeenMessageId, writeLastSeenMessageId]);

  const loadOlderMessages = useCallback(() => {
    if (!activeConversation?.id || isLoadingOlderMessagesRef.current || isMessagesLoading) return;
    if (!hasMoreOlderMessagesRef.current) return;

    const oldestMessageId = Number(messagesRef.current[0]?.id || 0);
    if (!oldestMessageId) return;

    const scrollNode = messagesScrollRef.current;
    if (scrollNode) {
      olderMessagesRestoreRef.current = {
        scrollHeight: scrollNode.scrollHeight,
        scrollTop: scrollNode.scrollTop
      };
    }

    loadMessages(activeConversation.id, undefined, {
      silent: true,
      beforeId: oldestMessageId
    });
  }, [activeConversation?.id, isMessagesLoading, loadMessages]);

  const loadUnreadConversationCounts = useCallback(
    async (conversationList, signal) => {
      if (!apiUrl || !currentUser?.id || !Array.isArray(conversationList) || conversationList.length === 0) {
        setUnreadConversationCounts({});
        return;
      }

      try {
        const results = await Promise.all(
          conversationList.map(async (conversation) => {
            const response = await apiFetch(
              `${apiUrl}/get-messages.php?uid=${currentUser.id}&conversation_id=${conversation.id}`,
              {
                credentials: "include",
                cache: "no-store",
                signal
              }
            );
            const data = await response.json();
            const conversationMessages = Array.isArray(data?.messages) ? data.messages : [];
            const storedLastSeenMessageId = readLastSeenMessageId(conversation.id);
            const effectiveLastSeenMessageId =
              storedLastSeenMessageId ||
              (hasCurrentUserParticipation(conversationMessages)
                ? getLatestVisibleMessageId(conversationMessages)
                : 0);
            if (!storedLastSeenMessageId && effectiveLastSeenMessageId) {
              writeLastSeenMessageId(conversation.id, effectiveLastSeenMessageId);
            }
            const unreadCount = conversationMessages.filter(
              (message) =>
                message?.message_type !== "system" &&
                Number(message?.id) > Number(effectiveLastSeenMessageId) &&
                Number(message?.user_id) !== Number(currentUser.id)
            ).length;

            return [conversation.id, unreadCount, getLatestVisibleMessageId(conversationMessages)];
          })
        );

        if (signal?.aborted) return;

        setUnreadConversationCounts(
          Object.fromEntries(
            results.map(([conversationId, unreadCount]) => [String(conversationId), unreadCount])
          )
        );
        setLatestConversationMessageIds(
          Object.fromEntries(
            results.map(([conversationId, _unreadCount, latestMessageId]) => [
              String(conversationId),
              latestMessageId
            ])
          )
        );
      } catch (error) {
        if (!signal?.aborted) {
          console.error("Failed to load unread conversation counts:", error);
        }
      }
    },
    [apiUrl, currentUser, getLatestVisibleMessageId, hasCurrentUserParticipation, readLastSeenMessageId, writeLastSeenMessageId]
  );

  useEffect(() => {
    if (!isComposerOpen) return undefined;

    const scrollY = window.scrollY;
    const previousBodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow
    };

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.position = previousBodyStyle.position;
      document.body.style.top = previousBodyStyle.top;
      document.body.style.width = previousBodyStyle.width;
      document.body.style.overflow = previousBodyStyle.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [isComposerOpen]);

  useEffect(() => {
    if (!isComposerOpen || !apiUrl || !currentUser?.id) return undefined;
    if (!trimmedUserQuery) {
      setUserResults([]);
      setIsUsersLoading(false);
      setUsersError("");
      return undefined;
    }

    const requestId = ++searchRequestRef.current;
    const controller = new AbortController();
    const timerId = window.setTimeout(() => {
      setIsUsersLoading(true);
      setUsersError("");

      const query = new URLSearchParams({
        uid: String(currentUser.id),
        q: trimmedUserQuery
      });

      apiFetch(`${apiUrl}/search-users.php?${query.toString()}`, {
        credentials: "include",
        cache: "no-store",
        signal: controller.signal
      })
        .then((response) => response.json())
        .then((data) => {
          if (searchRequestRef.current !== requestId) return;
          if (!data?.success) {
            setUserResults([]);
            setUsersError(data?.error || "Couldn't load users right now.");
            return;
          }

          setUserResults(Array.isArray(data.users) ? data.users : []);
        })
        .catch((error) => {
          if (controller.signal.aborted || searchRequestRef.current !== requestId) return;
          setUserResults([]);
          setUsersError(error?.message || "Couldn't load users right now.");
        })
        .finally(() => {
          if (searchRequestRef.current === requestId) {
            setIsUsersLoading(false);
          }
        });
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timerId);
    };
  }, [apiUrl, currentUser, isComposerOpen, trimmedUserQuery]);

  useEffect(() => {
    if (chatEditMode !== "members" || !apiUrl || !currentUser?.id || !menuConversation?.id) {
      return undefined;
    }

    if (!trimmedMemberQuery) {
      setMemberResults([]);
      setIsMemberSearchLoading(false);
      setMemberSearchError("");
      return undefined;
    }

    const requestId = ++memberSearchRequestRef.current;
    const controller = new AbortController();
    const timerId = window.setTimeout(() => {
      setIsMemberSearchLoading(true);
      setMemberSearchError("");

      const query = new URLSearchParams({
        uid: String(currentUser.id),
        q: trimmedMemberQuery
      });

      apiFetch(`${apiUrl}/search-users.php?${query.toString()}`, {
        credentials: "include",
        cache: "no-store",
        signal: controller.signal
      })
        .then((response) => response.json())
        .then((data) => {
          if (memberSearchRequestRef.current !== requestId) return;
          if (!data?.success) {
            setMemberResults([]);
            setMemberSearchError(data?.error || "Couldn't load users right now.");
            return;
          }

          setMemberResults(Array.isArray(data.users) ? data.users : []);
        })
        .catch((error) => {
          if (controller.signal.aborted || memberSearchRequestRef.current !== requestId) return;
          setMemberResults([]);
          setMemberSearchError(error?.message || "Couldn't load users right now.");
        })
        .finally(() => {
          if (memberSearchRequestRef.current === requestId) {
            setIsMemberSearchLoading(false);
          }
        });
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timerId);
    };
  }, [apiUrl, chatEditMode, currentUser, menuConversation?.id, trimmedMemberQuery]);

  useEffect(() => {
    if (!isOpen || !apiUrl || !currentUser?.id) return undefined;
    const controller = new AbortController();
    // Silent if we already have conversations from a previous open
    loadConversations(controller.signal, { silent: conversationsRef.current.length > 0 });
    return () => controller.abort();
  }, [apiUrl, currentUser, isOpen, loadConversations]);

  useEffect(() => {
    if (!isOpen || activeConversation?.id || !conversations.length) return undefined;

    const controller = new AbortController();
    loadUnreadConversationCounts(conversations, controller.signal);

    const intervalId = window.setInterval(() => {
      loadConversations(undefined, { silent: true });
      loadUnreadConversationCounts(conversations);
    }, 5000);

    return () => {
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, [activeConversation?.id, conversations, isOpen, loadConversations, loadUnreadConversationCounts]);

  useEffect(() => {
    if (!isOpen) return;
    setIsComposerOpen(false);
    setGroupName("");
    setUserQuery("");
    setUserResults([]);
    setSelectedUsers([]);
    setIsUsersLoading(false);
    setUsersError("");
    setIsCreatingChat(false);
    setCreateChatError("");
    setOpenMenuConversation(null);
    setChatEditMode(null);
    setEditChatName("");
    setMemberQuery("");
    setMemberResults([]);
    setSelectedNewMembers([]);
    setIsChatEditing(false);
    setChatEditError("");
    setIsMemberSearchLoading(false);
    setMemberSearchError("");
    setOpenSwipeMessageId(null);
    clearUnreadDivider();
  }, [clearUnreadDivider, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  const closeModal = useCallback(() => {
    if (isClosing) return;
    clearUnreadDivider({ markSeen: true });
    setIsClosing(true);
    window.setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 160);
  }, [clearUnreadDivider, isClosing, onClose]);

  useEffect(() => {
    const observers = quoteResizeObserversRef.current;
    return () => {
      window.clearTimeout(highlightReplyTimerRef.current);
      window.clearTimeout(unreadDismissTimerRef.current);
      observers.forEach((observer) => observer.disconnect());
      observers.clear();
    };
  }, []);

  useEffect(() => {
    if (!initialQuote?.id || !initialQuote?.quoteText) return;
    setQuotedMessage({
      messageId: null,
      sourceMessageId: null,
      quoteText: initialQuote.quoteText,
      quoteType: initialQuote.quoteType || "book"
    });
    onInitialQuoteApplied?.();
  }, [initialQuote, onInitialQuoteApplied]);

  useEffect(() => {
    if (!activeConversation?.id) {
      setMessages([]);
      setMessageError("");
      setIsLoadingOlderMessages(false);
      hasPagedOlderMessagesRef.current = false;
      hasMoreOlderMessagesRef.current = true;
      olderMessagesRestoreRef.current = null;
      clearUnreadDivider();
      return undefined;
    }
    // Modal closed — keep existing messages so they're instant on reopen
    if (!isOpen) return undefined;

    const controller = new AbortController();
    // Silent if we already have messages from a previous open
    loadMessages(activeConversation.id, controller.signal, { silent: messagesRef.current.length > 0 });

    const intervalId = window.setInterval(() => {
      if (!isLoadingOlderMessagesRef.current) {
        loadMessages(activeConversation.id, undefined, { silent: true });
      }
    }, 4000);

    return () => {
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, [activeConversation?.id, clearUnreadDivider, isOpen, loadMessages]);

  useEffect(() => {
    hasAutoJumpedUnreadRef.current = false;
    clearUnreadDivider();
    hasPagedOlderMessagesRef.current = false;
    hasMoreOlderMessagesRef.current = true;
    olderMessagesRestoreRef.current = null;
    setIsLoadingOlderMessages(false);
    shouldEvaluateUnreadOnEntryRef.current = Boolean(activeConversation?.id);
  }, [activeConversation?.id, clearUnreadDivider]);

  useEffect(() => {
    if (!isOpen || !activeConversation?.id) return;

    hasAutoJumpedUnreadRef.current = false;
    clearUnreadDivider();
    shouldEvaluateUnreadOnEntryRef.current = true;
    shouldScrollToBottomOnEntryRef.current = true;
    entryLastSeenMessageIdRef.current = readLastSeenMessageId(activeConversation.id);
  }, [activeConversation?.id, clearUnreadDivider, isOpen, readLastSeenMessageId]);

  useEffect(() => {
    if (!activeConversation || !composerRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const updateComposerHeight = () => {
      setComposerHeight(composerRef.current?.offsetHeight || 88);
    };

    updateComposerHeight();
    const observer = new ResizeObserver(updateComposerHeight);
      observer.observe(composerRef.current);

    return () => observer.disconnect();
  }, [activeConversation, quotedMessage]);

  useEffect(() => {
    const scrollNode = messagesScrollRef.current;
    if (!scrollNode) return;
    if (!shouldStickToBottomRef.current && !forceScrollToBottomRef.current) return;

    window.requestAnimationFrame(() => {
      scrollNode.scrollTop = scrollNode.scrollHeight;
      shouldStickToBottomRef.current = true;
      forceScrollToBottomRef.current = false;
    });
  }, [messages, composerHeight]);

  useEffect(() => {
    if (!olderMessagesRestoreRef.current) return;

    const restoreState = olderMessagesRestoreRef.current;
    olderMessagesRestoreRef.current = null;

    window.requestAnimationFrame(() => {
      const scrollNode = messagesScrollRef.current;
      if (!scrollNode) return;
      const addedHeight = scrollNode.scrollHeight - restoreState.scrollHeight;
      scrollNode.scrollTop = restoreState.scrollTop + addedHeight;
    });
  }, [messages]);

  useEffect(() => {
    if (!activeConversation?.id || !shouldScrollToBottomOnEntryRef.current || isMessagesLoading) {
      return;
    }

    const scrollNode = messagesScrollRef.current;
    if (!scrollNode) return;

    window.requestAnimationFrame(() => {
      scrollNode.scrollTop = scrollNode.scrollHeight;
      shouldStickToBottomRef.current = true;
      forceScrollToBottomRef.current = false;
      shouldScrollToBottomOnEntryRef.current = false;
    });
  }, [activeConversation?.id, isMessagesLoading, messages, composerHeight]);

  useEffect(() => {
    if (!isOpen || !activeConversation?.id || isMessagesLoading || messageError) return;

    const visibleMessages = messages.filter(
      (message) => message?.message_type !== "system" && Number(message?.id) > 0
    );
    const latestVisibleMessageId = getLatestVisibleMessageId(visibleMessages);
    if (!latestVisibleMessageId) return;

    if (
      unreadDividerConversationRef.current === activeConversation.id &&
      Number(unreadDividerMessageId)
    ) {
      return;
    }

    if (!shouldEvaluateUnreadOnEntryRef.current) {
      writeLastSeenMessageId(activeConversation.id, latestVisibleMessageId);
      return;
    }

    const lastSeenMessageId =
      entryLastSeenMessageIdRef.current != null
        ? Number(entryLastSeenMessageIdRef.current)
        : readLastSeenMessageId(activeConversation.id);
    if (!lastSeenMessageId) {
      writeLastSeenMessageId(activeConversation.id, latestVisibleMessageId);
      shouldEvaluateUnreadOnEntryRef.current = false;
      entryLastSeenMessageIdRef.current = null;
      return;
    }

    const firstUnreadMessage = visibleMessages.find(
      (message) =>
        Number(message.id) > Number(lastSeenMessageId) &&
        Number(message.user_id) !== Number(currentUser.id)
    );

    if (!firstUnreadMessage) {
      if (latestVisibleMessageId !== lastSeenMessageId) {
        writeLastSeenMessageId(activeConversation.id, latestVisibleMessageId);
      }
      shouldEvaluateUnreadOnEntryRef.current = false;
      entryLastSeenMessageIdRef.current = null;
      return;
    }

    const firstUnreadMessageId = Number(firstUnreadMessage.id);
    const firstUnreadLabel = formatUnreadDividerTime(firstUnreadMessage.created_at);
    const dividerChanged =
      unreadDividerConversationRef.current !== activeConversation.id ||
      Number(unreadDividerMessageId) !== firstUnreadMessageId;

    unreadDividerConversationRef.current = activeConversation.id;
    setUnreadDividerMessageId(firstUnreadMessageId);
    setUnreadDividerLabel(firstUnreadLabel);

    window.clearTimeout(unreadDismissTimerRef.current);
    unreadDismissTimerRef.current = window.setTimeout(() => {
      clearUnreadDivider({ markSeen: true });
    }, UNREAD_DIVIDER_DURATION_MS);
    shouldEvaluateUnreadOnEntryRef.current = false;
    entryLastSeenMessageIdRef.current = null;

    if (dividerChanged || !hasAutoJumpedUnreadRef.current) {
      window.requestAnimationFrame(() => {
        const targetNode = messageNodeRefs.current.get(firstUnreadMessageId);
        if (targetNode) {
          targetNode.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
          shouldStickToBottomRef.current = false;
          forceScrollToBottomRef.current = false;
          hasAutoJumpedUnreadRef.current = true;
        }
      });
    }
  }, [
    activeConversation?.id,
    clearUnreadDivider,
    currentUser.id,
    getLatestVisibleMessageId,
    isOpen,
    isMessagesLoading,
    messageError,
    messages,
    readLastSeenMessageId,
    unreadDividerMessageId,
    writeLastSeenMessageId
  ]);

  const toggleUserSelection = (user) => {
    setSelectedUsers((previous) => {
      const alreadySelected = previous.some((item) => item.id === user.id);
      if (alreadySelected) {
        return previous.filter((item) => item.id !== user.id);
      }
      return [...previous, user];
    });
  };

  const toggleNewMemberSelection = (user) => {
    setSelectedNewMembers((previous) => {
      const alreadySelected = previous.some((item) => Number(item.id) === Number(user.id));
      if (alreadySelected) {
        return previous.filter((item) => Number(item.id) !== Number(user.id));
      }
      return [...previous, user];
    });
  };

  const visibleUserResults = userResults.filter(
    (user) => !selectedUsers.some((selectedUser) => selectedUser.id === user.id)
  );

  const visibleMemberResults = memberResults.filter(
    (user) =>
      !(menuConversation?.members || []).some((member) => Number(member.id) === Number(user.id)) &&
      !selectedNewMembers.some((selectedUser) => Number(selectedUser.id) === Number(user.id))
  );

  const handleOpenConversation = (conversation) => {
    const unreadCount = Number(unreadConversationCounts[String(conversation.id)] || 0);
    const hasUnreadMessages = unreadCount > 0;

    forceScrollToBottomRef.current = !hasUnreadMessages;
    shouldStickToBottomRef.current = !hasUnreadMessages;
    shouldEvaluateUnreadOnEntryRef.current = hasUnreadMessages;
    shouldScrollToBottomOnEntryRef.current = !hasUnreadMessages;
    entryLastSeenMessageIdRef.current = readLastSeenMessageId(conversation.id);
    const latestConversationMessageId = Number(latestConversationMessageIds[String(conversation.id)] || 0);
    if (hasUnreadMessages && latestConversationMessageId) {
      writeLastSeenMessageId(conversation.id, latestConversationMessageId);
    }
    setUnreadConversationCounts((previous) => ({
      ...previous,
      [String(conversation.id)]: 0
    }));
    setActiveConversation(conversation);
    setMessages([]);
    setDraftMessage("");
    setMessageError("");
    setOpenMenuConversation(null);
    setOpenSwipeMessageId(null);
  };

  const bindQuoteMeasure = useCallback((messageId, node) => {
    const previousObserver = quoteResizeObserversRef.current.get(messageId);
    if (previousObserver) {
      previousObserver.disconnect();
      quoteResizeObserversRef.current.delete(messageId);
    }

    if (!node) {
      return;
    }

    const updateWidth = () => {
      const nextWidth = Math.ceil(node.getBoundingClientRect().width);
      setQuotedBubbleWidths((previous) =>
        previous[messageId] === nextWidth
          ? previous
          : { ...previous, [messageId]: nextWidth }
      );
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    quoteResizeObserversRef.current.set(messageId, observer);
  }, []);

  const beginMessageSwipe = (messageId, clientX, clientY) => {
    swipeStateRef.current = {
      messageId,
      startX: clientX,
      startY: clientY,
      deltaX: 0,
      horizontal: false
    };
  };

  const updateMessageSwipe = (event, clientX, clientY) => {
    const swipeState = swipeStateRef.current;
    if (!swipeState.messageId) return;

    const deltaX = clientX - swipeState.startX;
    const deltaY = clientY - swipeState.startY;
    const isSwipeOpen = openSwipeMessageId === swipeState.messageId;

    if (!swipeState.horizontal) {
      if (Math.abs(deltaX) < 12 && Math.abs(deltaY) < 12) {
        return;
      }

      const isOpeningSwipe = !isSwipeOpen && deltaX < 0;
      const isClosingSwipe = isSwipeOpen && deltaX > 0;

      if (Math.abs(deltaX) > Math.abs(deltaY) && (isOpeningSwipe || isClosingSwipe)) {
        swipeState.horizontal = true;
      } else {
        swipeStateRef.current = {
          messageId: null,
          startX: 0,
          startY: 0,
          deltaX: 0,
          horizontal: false
        };
        return;
      }
    }

    swipeState.deltaX = deltaX;
    if (event.cancelable) {
      event.preventDefault();
    }
  };

  const endMessageSwipe = () => {
    const swipeState = swipeStateRef.current;
    if (!swipeState.messageId) return;

    if (swipeState.horizontal && swipeState.deltaX <= -34) {
      setOpenSwipeMessageId(swipeState.messageId);
    } else if (swipeState.horizontal && swipeState.deltaX >= 16) {
      setOpenSwipeMessageId(null);
    }

    swipeStateRef.current = {
      messageId: null,
      startX: 0,
      startY: 0,
      deltaX: 0,
      horizontal: false
    };
  };

  const openRenameChat = () => {
    if (!menuConversation) return;
    setEditChatName(menuConversation.name || "");
    setChatEditError("");
    setChatEditMode("rename");
    setOpenMenuConversation(null);
  };

  const openAddMembers = () => {
    setMemberQuery("");
    setMemberResults([]);
    setSelectedNewMembers([]);
    setChatEditError("");
    setMemberSearchError("");
    setChatEditMode("members");
    setOpenMenuConversation(null);
  };

  const handleLeaveChat = async (conversationId) => {
    if (!apiUrl || !currentUser?.id) return;

    try {
      const response = await apiFetch(`${apiUrl}/leave-chat.php`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          uid: currentUser.id,
          conversationId
        })
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Couldn't leave chat right now.");
      }
      setConversations((previous) =>
        previous.filter((conversation) => conversation.id !== conversationId)
      );
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
      setOpenMenuConversation(null);
    } catch (error) {
      window.alert(error?.message || "Couldn't leave chat right now.");
    }
  };

  const handleDeleteChat = async (conversationId) => {
    if (!apiUrl || !currentUser?.id) return;

    try {
      const response = await apiFetch(`${apiUrl}/delete-chat.php`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          uid: currentUser.id,
          conversationId
        })
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Couldn't delete chat right now.");
      }
      setConversations((previous) =>
        previous.filter((conversation) => conversation.id !== conversationId)
      );
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
      setOpenMenuConversation(null);
    } catch (error) {
      window.alert(error?.message || "Couldn't delete chat right now.");
    }
  };

  const handleRenameChat = async () => {
    const trimmedName = editChatName.trim();
    if (!trimmedName || !menuConversation?.id || !apiUrl || !currentUser?.id) return;

    setIsChatEditing(true);
    setChatEditError("");

    try {
      const response = await apiFetch(`${apiUrl}/rename-chat.php`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          uid: currentUser.id,
          conversationId: menuConversation.id,
          name: trimmedName
        })
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Couldn't change the chat name right now.");
      }

      mergeConversationState(data.conversation);
      setChatEditMode(null);
      setEditChatName("");
    } catch (error) {
      setChatEditError(error?.message || "Couldn't change the chat name right now.");
    } finally {
      setIsChatEditing(false);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedNewMembers.length || !menuConversation?.id || !apiUrl || !currentUser?.id) {
      return;
    }

    setIsChatEditing(true);
    setChatEditError("");

    try {
      const response = await apiFetch(`${apiUrl}/add-chat-members.php`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          uid: currentUser.id,
          conversationId: menuConversation.id,
          memberIds: selectedNewMembers.map((user) => user.id)
        })
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Couldn't add members right now.");
      }

      mergeConversationState(data.conversation);
      setChatEditMode(null);
      setMemberQuery("");
      setMemberResults([]);
      setSelectedNewMembers([]);
    } catch (error) {
      setChatEditError(error?.message || "Couldn't add members right now.");
    } finally {
      setIsChatEditing(false);
    }
  };

  const handleCreateChat = async () => {
    const trimmedGroupName = groupName.trim();

    if (!trimmedGroupName || !selectedUsers.length || !apiUrl || !currentUser?.id) {
      return;
    }

    setIsCreatingChat(true);
    setCreateChatError("");

    try {
      const response = await apiFetch(`${apiUrl}/create-chat.php`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          uid: currentUser.id,
          name: trimmedGroupName,
          memberIds: selectedUsers.map((user) => user.id)
        })
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Couldn't create chat right now.");
      }

      const nextConversation = {
        ...data.conversation,
        created_by: data?.conversation?.created_by || currentUser.id,
        is_creator:
          typeof data?.conversation?.is_creator === "boolean"
            ? data.conversation.is_creator
            : true,
        members:
          Array.isArray(data?.conversation?.members) && data.conversation.members.length > 0
            ? data.conversation.members
            : [
                {
                  id: currentUser.id,
                  display_name:
                    currentUser.displayName || currentUser.display_name || "You",
                  profile_picture:
                    currentUser.profilePicture || currentUser.profile_picture || null
                },
                ...selectedUsers
              ],
        member_names:
          Array.isArray(data?.conversation?.member_names) &&
          data.conversation.member_names.length > 0
            ? data.conversation.member_names
            : [
                currentUser.displayName || currentUser.display_name || "You",
                ...selectedUsers.map((user) => user.display_name)
              ]
      };

      setConversations((previous) => [nextConversation, ...previous]);
      setGroupName("");
      setUserQuery("");
      setUserResults([]);
      setSelectedUsers([]);
      setIsComposerOpen(false);
      setActiveConversation(nextConversation);
    } catch (error) {
      setCreateChatError(error?.message || "Couldn't create chat right now.");
    } finally {
      setIsCreatingChat(false);
    }
  };

  const handleSendMessage = async () => {
    const trimmedMessage = draftMessage.trim();

    if (!trimmedMessage || !activeConversation?.id || !apiUrl || !currentUser?.id) {
      return;
    }

    setIsSendingMessage(true);
    setMessageError("");

    try {
      const response = await apiFetch(`${apiUrl}/send-message.php`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          uid: currentUser.id,
          conversationId: activeConversation.id,
          text: trimmedMessage,
          quotedText: quotedMessage?.quoteText || "",
          quoteType: quotedMessage?.quoteType || "",
          quotedMessageId: quotedMessage?.quoteType === "reply" ? quotedMessage?.sourceMessageId || quotedMessage?.messageId || 0 : 0
        })
      });
      const data = await response.json();

        if (!response.ok || !data?.success) {
          throw new Error(data?.error || "Couldn't send message right now.");
        }

        setMessages((previous) => [...previous, data.message]);
        clearUnreadDivider({
          markSeen: true,
          messageId: Number(data.message?.id || 0)
        });
        setDraftMessage("");
      setQuotedMessage(null);
      setOpenSwipeMessageId(null);
      forceScrollToBottomRef.current = true;
      shouldStickToBottomRef.current = true;
      setConversations((previous) => {
        const updatedConversation = {
          ...activeConversation,
          created_label: data.message.created_label || "just now"
        };
        const withoutConversation = previous.filter(
          (conversation) => conversation.id !== activeConversation.id
        );
        return [updatedConversation, ...withoutConversation];
      });
    } catch (error) {
      setMessageError(error?.message || "Couldn't send message right now.");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const jumpToQuotedReply = (message) => {
    if (message.quote_type !== "reply" || !message.quoted_message_id) return;
    const targetNode = messageNodeRefs.current.get(Number(message.quoted_message_id));
    if (!targetNode) return;

    targetNode.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
    setHighlightedReplyTargetId(Number(message.quoted_message_id));
    window.clearTimeout(highlightReplyTimerRef.current);
    highlightReplyTimerRef.current = window.setTimeout(() => {
      setHighlightedReplyTargetId(null);
    }, 1000);
  };

  const renderConversationList = () => (
    <>
      <div className="highlight-note-header reader-chat-sheet-header">
        <div className="reader-chat-sheet-heading">
          <h2 className="reader-chat-sheet-title mb-2">Direct Messages</h2>
          <p className="reader-chat-sheet-subtitle mb-0">Works across all books</p>
        </div>
        <button
          type="button"
          className="reader-chat-header-btn"
          aria-label="Start a new chat"
          onClick={() => setIsComposerOpen(true)}
        >
          <span className="material-symbols-rounded">chat_add_on</span>
        </button>
      </div>

      <div className="reader-chat-sheet-body">
        {!isOnline ? (
          <div className="reader-chat-offline-state">You're offline</div>
        ) : (
          <div className="reader-chat-list-shell">
            {conversations.map((conversation) => (
              <div key={conversation.id} className="reader-chat-thread">
                {(() => {
                  const unreadCount = Number(unreadConversationCounts[String(conversation.id)] || 0);
                  return (
                <div
                  className={`reader-chat-thread-row${unreadCount > 0 ? " reader-chat-thread-row--unread" : ""}`}
                  onClick={() => handleOpenConversation(conversation)}
                >
                  <div className="reader-chat-thread-main">
                    <div className="reader-chat-thread-name">
                      {conversation.name}
                      {unreadCount > 0 && (
                        <span className="reader-chat-thread-unread-count">{`\u2022 ${unreadCount}`}</span>
                      )}
                    </div>
                    <div className="reader-chat-thread-members">
                      <div className="reader-chat-thread-members-row">
                        <div className="reader-chat-thread-avatars" aria-hidden="true">
                          {(conversation.members || []).slice(0, 5).map((member, index) => (
                            <ReaderChatAvatar
                              key={`${conversation.id}-member-${member.id || index}`}
                              user={member}
                              className="reader-chat-thread-avatar"
                            />
                          ))}
                        </div>
                        <span className="reader-chat-thread-member-names">
                          {formatConversationMembers(conversation.member_names || [])}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="reader-chat-thread-side">
                    <button
                      type="button"
                      className="reader-chat-thread-menu"
                      aria-label="Chat options"
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenMenuConversation(conversation);
                      }}
                    >
                      <span className="material-symbols-rounded">more_horiz</span>
                    </button>
                    <div className="reader-chat-thread-age">
                      {conversation.created_label || "just now"}
                    </div>
                  </div>
                </div>
                  );
                })()}
                <div className="reader-chat-list-divider" />
              </div>
            ))}
            {isConversationsLoading && conversations.length === 0 && (
              <div className="reader-chat-loading-state">Loading chats...</div>
            )}
          </div>
        )}
      </div>
    </>
  );

  const renderConversationDetail = () => (
    <div className="reader-chat-detail">
      <div className="reader-chat-detail-header">
        <button
          type="button"
          className="reader-chat-detail-back"
          aria-label="Back to chats"
          onClick={() => {
            setActiveConversation(null);
            setQuotedMessage(null);
            setDraftMessage("");
            setOpenSwipeMessageId(null);
          }}
        >
          <span className="material-symbols-rounded">chevron_left</span>
        </button>

        <div className="reader-chat-detail-heading">
          <div className="reader-chat-detail-title fw-bold mb-0">{activeConversation.name}</div>
          <div className="reader-chat-detail-subtitle mb-0">
            {getConversationSubtitle(activeConversation, currentUser.id)}
          </div>
        </div>

        <button
          type="button"
          className="reader-chat-detail-back reader-chat-detail-menu-btn"
          aria-label="Chat options"
          onClick={() => setOpenMenuConversation(activeConversation)}
        >
          <span className="material-symbols-rounded">more_horiz</span>
        </button>
      </div>

      {!isOnline ? (
        <div className="reader-chat-offline-state">You're offline</div>
      ) : (
        <>
          <div
            ref={messagesScrollRef}
            className="reader-chat-message-scroll"
            onScroll={() => {
              const scrollNode = messagesScrollRef.current;
              if (scrollNode) {
                const distanceFromBottom =
                  scrollNode.scrollHeight - scrollNode.scrollTop - scrollNode.clientHeight;
                shouldStickToBottomRef.current = distanceFromBottom <= 80;
                if (scrollNode.scrollTop <= 48) {
                  loadOlderMessages();
                }
              }
              if (openSwipeMessageId) {
                setOpenSwipeMessageId(null);
              }
            }}
          >
            {isLoadingOlderMessages && (
              <div className="reader-chat-loading-state px-4 pb-3">Loading earlier messages...</div>
            )}

            {isMessagesLoading && (
              <div className="reader-chat-loading-state p-4">Loading messages...</div>
            )}

            {!isMessagesLoading && messageError && (
              <div className="reader-chat-loading-state">{messageError}</div>
            )}

            {!isMessagesLoading &&
              !messageError &&
              messages.map((message, index) => {
            if (message.message_type === "system") {
              return (
                <div
                  key={message.id}
                  className="reader-chat-system-message"
                >
                  {message.text}
                </div>
              );
            }

            const isOwnMessage = Number(message.user_id) === Number(currentUser.id);
            const previousMessage = messages[index - 1];
            const shouldShowMeta =
              !previousMessage || Number(previousMessage.user_id) !== Number(message.user_id);

            return (
              <div key={`message-block-${message.id}`}>
            {Number(unreadDividerMessageId) === Number(message.id) && (
                  <div className="reader-chat-unread-divider">
                    <span className="reader-chat-unread-divider__line" />
                    <span className="reader-chat-unread-divider__label">
                      New messages
                      {unreadDividerLabel ? ` • ${unreadDividerLabel}` : ""}
                    </span>
                    <span className="reader-chat-unread-divider__line" />
                  </div>
                )}

                <div
                  key={message.id}
                  className={`reader-chat-message-row${isOwnMessage ? " reader-chat-message-row--own" : ""}${message.quoted_text ? " reader-chat-message-row--quoted" : ""}${shouldShowMeta ? " reader-chat-message-row--new-sender" : ""}`}
                >
                {shouldShowMeta && (
                  <div
                    className={`reader-chat-message-meta${isOwnMessage ? " reader-chat-message-meta--own" : ""}`}
                  >
                    {!isOwnMessage && (
                      <ReaderChatAvatar
                        user={message}
                        className="reader-chat-message-avatar"
                      />
                    )}
                    <span className="reader-chat-message-author">
                      {isOwnMessage ? "you" : message.display_name}
                    </span>
                    <span className="reader-chat-message-dot">•</span>
                    <span className="reader-chat-message-time">{message.created_label}</span>
                  </div>
                )}

                <div
                  className={`reader-chat-message-swipe${openSwipeMessageId === message.id ? " reader-chat-message-swipe--open" : ""}${isOwnMessage ? " reader-chat-message-swipe--own" : ""}`}
                >
                  <button
                    type="button"
                    className="reader-chat-message-quote-action"
                    aria-label="Quote message"
                    onClick={() => {
                      setQuotedMessage({
                        messageId: message.id,
                        sourceMessageId: message.id,
                        quoteText: message.text,
                        quoteType: "reply"
                      });
                      setOpenSwipeMessageId(null);
                    }}
                  >
                    <span className="material-symbols-rounded">prompt_suggestion</span>
                  </button>

                  <div
                    className={`reader-chat-message-card${isOwnMessage ? " reader-chat-message-card--own" : ""}`}
                    ref={(node) => {
                      if (node) {
                        messageNodeRefs.current.set(Number(message.id), node);
                      } else {
                        messageNodeRefs.current.delete(Number(message.id));
                      }
                    }}
                    onTouchStart={(event) => {
                      const touch = event.touches[0];
                      if (!touch) return;
                      beginMessageSwipe(message.id, touch.clientX, touch.clientY);
                    }}
                    onTouchMove={(event) => {
                      const touch = event.touches[0];
                      if (!touch) return;
                      updateMessageSwipe(event, touch.clientX, touch.clientY);
                    }}
                    onTouchEnd={endMessageSwipe}
                    onTouchCancel={endMessageSwipe}
                    onMouseDown={(event) =>
                      beginMessageSwipe(message.id, event.clientX, event.clientY)
                    }
                    onMouseMove={(event) => updateMessageSwipe(event, event.clientX, event.clientY)}
                    onMouseUp={endMessageSwipe}
                    onMouseLeave={endMessageSwipe}
                  >
                    <div
                      className={`reader-chat-message-content${isOwnMessage ? " reader-chat-message-content--own" : ""}${message.quoted_text ? " reader-chat-message-content--quoted" : ""}${highlightedReplyTargetId === Number(message.id) ? " reader-chat-message-content--flash" : ""}`}
                    >
                      {message.quoted_text && (
                        <button
                          type="button"
                          className={`reader-chat-quote${isOwnMessage ? " reader-chat-quote--own" : ""}${message.quote_type === "book" ? " reader-chat-quote--book" : ""}${message.quote_type === "reply" && message.quoted_message_id ? " reader-chat-quote--interactive" : ""}`}
                          ref={(node) => bindQuoteMeasure(message.id, node)}
                          onClick={() => jumpToQuotedReply(message)}
                          disabled={message.quote_type !== "reply" || !message.quoted_message_id}
                        >
                          <span className="reader-chat-quote-line" />
                          <span className={`reader-chat-quote-text${message.quote_type === "book" ? " reader-chat-quote-text--book" : ""}`}>
                            {message.quoted_text}
                          </span>
                        </button>
                      )}

                      <div
                        className={`reader-chat-message-bubble${isOwnMessage ? " reader-chat-message-bubble--own" : ""}`}
                        style={
                          message.quoted_text && quotedBubbleWidths[message.id]
                            ? { minWidth: `${quotedBubbleWidths[message.id]}px` }
                            : undefined
                        }
                      >
                        {message.text}
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            );
              })}
          </div>

          <div
            ref={composerRef}
            className="reader-chat-composer-bar"
          >
            {quotedMessage && (
              <div className={`reader-chat-composer-quote${quotedMessage.quoteType === "book" ? " reader-chat-composer-quote--book" : ""}`}>
                <span className="reader-chat-quote-line" />
                <span className={`reader-chat-quote-text${quotedMessage.quoteType === "book" ? " reader-chat-quote-text--book reader-chat-quote-text--book-preview" : ""}`}>
                  {quotedMessage.quoteText}
                </span>
                <button
                  type="button"
                  className="reader-chat-composer-quote-dismiss"
                  onClick={() => setQuotedMessage(null)}
                  aria-label="Remove quoted text"
                >
                  <span className="material-symbols-rounded">close</span>
                </button>
              </div>
            )}

            <div className="reader-chat-composer-actions">
              <div className="btn-neutral reader-chat-input-wrap">
                <textarea
                  className="reader-chat-input"
                  placeholder="Type here..."
                  value={draftMessage}
                  rows={1}
                  onChange={(event) => setDraftMessage(event.target.value)}
                />
                <button
                  type="button"
                  className="btn-cta reader-chat-send-btn"
                  onClick={handleSendMessage}
                  disabled={!draftMessage.trim() || isSendingMessage}
                >
                  <span className="material-symbols-rounded">
                    {isSendingMessage ? "progress_activity" : "send"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      className={`highlight-sheet-overlay reader-chat-overlay${isClosing ? " reader-chat-overlay--closing" : ""}`}
      style={!isOpen && !isClosing ? { display: "none" } : undefined}
      onClick={closeModal}
    >
      <div
        className={`reader-chat-sheet${isClosing ? " reader-chat-sheet--closing" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        {activeConversation ? renderConversationDetail() : renderConversationList()}
      </div>

      {isComposerOpen && (
        <div
          className="reader-chat-composer-overlay"
          onClick={() => setIsComposerOpen(false)}
        >
          <div
            className="reader-chat-composer"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="reader-chat-composer-header">
              <div>
                <h3 className="reader-chat-composer-title mb-0">Start a New Chat</h3>
              </div>
              <button
                type="button"
                className="reader-chat-composer-close"
                aria-label="Close new chat"
                onClick={() => setIsComposerOpen(false)}
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>

            <div className="reader-chat-composer-body">
              <div className="reader-chat-composer-scroll">
                <div className="reader-chat-form-section">
                  <label className="reader-chat-field-label" htmlFor="reader-chat-group-name">
                    Group Chat Name
                  </label>
                  <input
                    id="reader-chat-group-name"
                    type="text"
                    className="btn-neutral reader-chat-text-input"
                    placeholder="Give the chat a name"
                    value={groupName}
                    onChange={(event) => setGroupName(event.target.value)}
                  />
                </div>

                <div className="reader-chat-form-section reader-chat-form-section--results">
                  <div className="reader-chat-section-heading">
                    <label className="reader-chat-field-label" htmlFor="reader-chat-user-search">
                      Add Readers
                    </label>
                    <span className="reader-chat-selection-count">
                      {selectedUsers.length} selected
                    </span>
                  </div>

                  <div className="btn-neutral reader-chat-user-search">
                    <input
                      id="reader-chat-user-search"
                      type="search"
                      className="reader-chat-search-input"
                      placeholder="Search all users"
                      value={userQuery}
                      onChange={(event) => setUserQuery(event.target.value)}
                    />
                  </div>

                  <div className="reader-chat-results-shell">
                    {selectedUsers.length > 0 && (
                      <div className="reader-chat-selected-users">
                        <div className="reader-chat-selected-header">
                          <span>Picked readers</span>
                          <button
                            type="button"
                            className="reader-chat-clear-btn"
                            onClick={() => setSelectedUsers([])}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="reader-chat-selected-grid">
                          {selectedUsers.map((user) => (
                            <button
                              key={`selected-${user.id}`}
                              type="button"
                              className="reader-chat-user-card reader-chat-user-card--selected"
                              onClick={() => toggleUserSelection(user)}
                            >
                              <ReaderChatAvatar user={user} />
                              <div className="reader-chat-user-meta">
                                <span className="reader-chat-user-name">{user.display_name}</span>
                                <span className="reader-chat-user-tag">Selected</span>
                              </div>
                              <span className="material-symbols-rounded reader-chat-user-check">
                                check_circle
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="reader-chat-search-results">
                      {trimmedUserQuery && isUsersLoading && (
                        <div className="reader-chat-results-state">Searching readers...</div>
                      )}

                      {trimmedUserQuery && !isUsersLoading && usersError && (
                        <div className="reader-chat-results-state">{usersError}</div>
                      )}

                      {trimmedUserQuery &&
                        !isUsersLoading &&
                        !usersError &&
                        visibleUserResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            className="reader-chat-user-card btn-neutral"
                            onClick={() => toggleUserSelection(user)}
                          >
                            <ReaderChatAvatar user={user} />
                            <div className="reader-chat-user-meta">
                              <span className="reader-chat-user-name">{user.display_name}</span>
                            </div>
                            <span className="material-symbols-rounded reader-chat-user-check">
                              add_circle
                            </span>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="reader-chat-composer-footer">
                {createChatError && (
                  <div className="reader-chat-create-error">{createChatError}</div>
                )}
                <button
                  type="button"
                  className="btn-cta reader-chat-create-btn"
                  onClick={handleCreateChat}
                  disabled={!groupName.trim() || !selectedUsers.length || isCreatingChat}
                >
                  {isCreatingChat ? "Creating..." : "Create Chat"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {openMenuConversation && (
        <div
          className="reader-chat-menu-overlay"
          onClick={(event) => {
            event.stopPropagation();
            setOpenMenuConversation(null);
          }}
        >
          <div
            className="reader-chat-menu-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <button className="reader-chat-menu-item reader-chat-menu-item--disabled">
              Mute Chat
            </button>
            <button
              className="reader-chat-menu-item"
              onClick={openAddMembers}
            >
              Add Members
            </button>
            <button
              className="reader-chat-menu-item"
              onClick={openRenameChat}
            >
              Change Chat Name
            </button>
            <button
              className="reader-chat-menu-item"
              onClick={() => handleLeaveChat(openMenuConversation.id)}
            >
              Leave Chat
            </button>
            <button
              className={`reader-chat-menu-item reader-chat-menu-item--danger${openMenuConversation.is_creator ? "" : " reader-chat-menu-item--disabled"}`}
              onClick={() => {
                if (!openMenuConversation.is_creator) return;
                handleDeleteChat(openMenuConversation.id);
              }}
            >
              Delete Chat
            </button>
          </div>
        </div>
      )}

      {chatEditMode && menuConversation && (
        <div
          className="reader-chat-composer-overlay"
          onClick={() => {
            setChatEditMode(null);
            setChatEditError("");
          }}
        >
          <div
            className="reader-chat-composer reader-chat-composer--compact"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="reader-chat-composer-header">
              <div>
                <h3 className="reader-chat-composer-title mb-0">
                  {chatEditMode === "rename" ? "Change GC Name" : "Add Members"}
                </h3>
              </div>
              <button
                type="button"
                className="reader-chat-composer-close"
                aria-label="Close chat editor"
                onClick={() => {
                  setChatEditMode(null);
                  setChatEditError("");
                }}
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>

            <div className="reader-chat-composer-body">
              <div className="reader-chat-composer-scroll">
                {chatEditMode === "rename" ? (
                  <div className="reader-chat-form-section">
                    <label className="reader-chat-field-label" htmlFor="reader-chat-edit-name">
                      Group Chat Name
                    </label>
                    <input
                      id="reader-chat-edit-name"
                      type="text"
                      className="btn-neutral reader-chat-text-input"
                      value={editChatName}
                      onChange={(event) => setEditChatName(event.target.value)}
                    />
                  </div>
                ) : (
                  <div className="reader-chat-form-section reader-chat-form-section--results">
                    <div className="reader-chat-section-heading">
                      <label className="reader-chat-field-label" htmlFor="reader-chat-member-search">
                        Add Readers
                      </label>
                      <span className="reader-chat-selection-count">
                        {selectedNewMembers.length} selected
                      </span>
                    </div>

                    <div className="btn-neutral reader-chat-user-search">
                      <input
                        id="reader-chat-member-search"
                        type="search"
                        className="reader-chat-search-input"
                        placeholder="Search all users"
                        value={memberQuery}
                        onChange={(event) => setMemberQuery(event.target.value)}
                      />
                    </div>

                    <div className="reader-chat-results-shell">
                      {selectedNewMembers.length > 0 && (
                        <div className="reader-chat-selected-users">
                          <div className="reader-chat-selected-header">
                            <span>Picked readers</span>
                            <button
                              type="button"
                              className="reader-chat-clear-btn"
                              onClick={() => setSelectedNewMembers([])}
                            >
                              Clear
                            </button>
                          </div>
                          <div className="reader-chat-selected-grid">
                            {selectedNewMembers.map((user) => (
                              <button
                                key={`member-selected-${user.id}`}
                                type="button"
                                className="reader-chat-user-card reader-chat-user-card--selected"
                                onClick={() => toggleNewMemberSelection(user)}
                              >
                                <ReaderChatAvatar user={user} />
                                <div className="reader-chat-user-meta">
                                  <span className="reader-chat-user-name">{user.display_name}</span>
                                  <span className="reader-chat-user-tag">Selected</span>
                                </div>
                                <span className="material-symbols-rounded reader-chat-user-check">
                                  check_circle
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="reader-chat-search-results">
                        {trimmedMemberQuery && isMemberSearchLoading && (
                          <div className="reader-chat-results-state">Searching readers...</div>
                        )}

                        {trimmedMemberQuery && !isMemberSearchLoading && memberSearchError && (
                          <div className="reader-chat-results-state">{memberSearchError}</div>
                        )}

                        {trimmedMemberQuery &&
                          !isMemberSearchLoading &&
                          !memberSearchError &&
                          visibleMemberResults.map((user) => (
                            <button
                              key={`member-result-${user.id}`}
                              type="button"
                              className="reader-chat-user-card btn-neutral"
                              onClick={() => toggleNewMemberSelection(user)}
                            >
                              <ReaderChatAvatar user={user} />
                              <div className="reader-chat-user-meta">
                                <span className="reader-chat-user-name">{user.display_name}</span>
                              </div>
                              <span className="material-symbols-rounded reader-chat-user-check">
                                add_circle
                              </span>
                            </button>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="reader-chat-composer-footer">
                {chatEditError && (
                  <div className="reader-chat-create-error">{chatEditError}</div>
                )}
                <button
                  type="button"
                  className="btn-cta reader-chat-create-btn"
                  onClick={chatEditMode === "rename" ? handleRenameChat : handleAddMembers}
                  disabled={
                    isChatEditing ||
                    (chatEditMode === "rename"
                      ? !editChatName.trim()
                      : !selectedNewMembers.length)
                  }
                >
                  {isChatEditing
                    ? "Saving..."
                    : chatEditMode === "rename"
                      ? "Save Name"
                      : "Add Members"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReaderChatModal;
